import { SubmissionMethod, SubmissionType } from "encamp-prisma/generated/browser";

/**
 * The RCRA waste "forms" entered through the guided submission stepper, each
 * defined by the `(submissionType, submissionMethod)` combos that constitute it.
 *
 * This is the single source of truth for "what is an 8700-12 / 8700-13." The
 * frontend stepper-eligibility check, the backend step config, the compliance
 * document-requirement rule, and the document-less review path all derive from
 * here — so the combos live in exactly one place.
 *
 * 8700-13 combos are registered now (for the stepper fast-follow), but only
 * 8700-12 is `stepperEligible` / `documentlessReview` today.
 */
export enum WasteSubmissionForm {
  RCRA_8700_12 = "RCRA_8700_12",
  RCRA_8700_13 = "RCRA_8700_13",
}

type SubmissionCombo = { type: SubmissionType; method: SubmissionMethod };

const RCRA_8700_12_COMBO: SubmissionCombo = {
  type: SubmissionType.HAZARDOUS_WASTE_NOTIFICATION,
  method: SubmissionMethod.RCRAINFO_MYRCRAID_MODULE,
};

/** The defining fact: each form is the set of its `(type, method)` combos. */
const FORM_COMBOS: Record<WasteSubmissionForm, ReadonlyArray<SubmissionCombo>> = {
  [WasteSubmissionForm.RCRA_8700_12]: [RCRA_8700_12_COMBO],
  [WasteSubmissionForm.RCRA_8700_13]: [
    {
      type: SubmissionType.BIENNIAL_REPORT,
      method: SubmissionMethod.RCRAINFO_BIENNIAL_REPORTING_MODULE,
    },
    {
      type: SubmissionType.ANNUAL_REPORT,
      method: SubmissionMethod.RCRAINFO_ANNUAL_REPORTING_MODULE,
    },
  ],
};

type FormCapabilities = {
  /** Entered through the guided stepper rather than the legacy upload flow. */
  stepperEligible: boolean;
  /** Reviewers approve the submission directly; there is no document to review. */
  documentlessReview: boolean;
  /** The compliance schema requires at least one submission document. */
  requiresSubmissionDocument: boolean;
};

/**
 * Human-readable form names, using the EPA form number plus the RCRAInfo-facing
 * description so users familiar with RCRAInfo can map our `(type, method)`
 * combos back to the form they know.
 */
const FORM_DISPLAY_NAME: Record<WasteSubmissionForm, string> = {
  [WasteSubmissionForm.RCRA_8700_12]: "EPA Form 8700-12 — Site Identification",
  [WasteSubmissionForm.RCRA_8700_13]: "EPA Form 8700-13 — Biennial/Annual Hazardous Waste Report",
};

const FORM_CAPABILITIES: Record<WasteSubmissionForm, FormCapabilities> = {
  [WasteSubmissionForm.RCRA_8700_12]: {
    stepperEligible: true,
    documentlessReview: true,
    requiresSubmissionDocument: false,
  },
  [WasteSubmissionForm.RCRA_8700_13]: {
    stepperEligible: false,
    documentlessReview: false,
    requiresSubmissionDocument: false,
  },
};

/**
 * Resolves the waste form for a `(type, method)` combo, or `undefined` when the
 * combo is not one of our forms. Accepts plain strings so both the Prisma enums
 * (backend) and the GraphQL enums (frontend) can be passed without casting.
 */
export function getWasteSubmissionForm(
  submissionType?: string | null,
  submissionMethod?: string | null
): WasteSubmissionForm | undefined {
  if (!submissionType || !submissionMethod) {
    return undefined;
  }
  return (Object.keys(FORM_COMBOS) as WasteSubmissionForm[]).find((form) =>
    FORM_COMBOS[form].some(
      (combo) => combo.type === submissionType && combo.method === submissionMethod
    )
  );
}

/**
 * Human-readable name for the waste form a `(type, method)` combo resolves to,
 * or `undefined` when the combo is not one of our forms. Accepts plain strings
 * so both the Prisma enums (backend) and the GraphQL enums (frontend) work.
 */
export function getWasteSubmissionFormName(
  submissionType?: string | null,
  submissionMethod?: string | null
): string | undefined {
  const form = getWasteSubmissionForm(submissionType, submissionMethod);
  return form === undefined ? undefined : FORM_DISPLAY_NAME[form];
}

/**
 * The `(type, method)` combo created when a user opts into the stepper, so the
 * create-submission dialog can offer "fill out an 8700-12" as a single choice
 * instead of asking for the type and method that happen to map to it. Today the
 * 8700-12 is the only stepper-eligible form and it has exactly one combo;
 * revisit this shape if a multi-combo form ever becomes stepper-eligible.
 * Returned as plain strings so both the Prisma enums (backend) and the GraphQL
 * enums (frontend) can consume it without casting.
 */
export function getStepperSubmissionCombo(): { type: string; method: string } {
  return RCRA_8700_12_COMBO;
}

/** True when the combo is entered through the stepper. */
export function isStepperEligibleSubmission(
  submissionType?: string | null,
  submissionMethod?: string | null
): boolean {
  const form = getWasteSubmissionForm(submissionType, submissionMethod);
  return form !== undefined && FORM_CAPABILITIES[form].stepperEligible;
}

/**
 * True when reviewers approve the submission directly (no document to review).
 *
 * `hasSubmissionSteps` is whether the submission has at least one
 * `SubmissionStep` row — the same marker the frontend uses to decide between
 * the stepper UI and the document-review UI (`usesStepper`). A combo match
 * alone is not enough: a legacy uploaded 8700-12 shares the combo but must
 * keep the document-review path so signing/document review is not bypassed.
 */
export function usesDocumentlessReview(
  submissionType: string | null | undefined,
  submissionMethod: string | null | undefined,
  hasSubmissionSteps: boolean
): boolean {
  const form = getWasteSubmissionForm(submissionType, submissionMethod);
  return form !== undefined && FORM_CAPABILITIES[form].documentlessReview && hasSubmissionSteps;
}

/**
 * True when the compliance schema should require a submission document. Defaults
 * to `true` for anything that is not a known document-exempt waste form.
 *
 * `hasSubmissionSteps` is whether the submission has at least one
 * `SubmissionStep` row (the stepper marker, mirroring the frontend's
 * `usesStepper`). Only stepper-created submissions are document-exempt; a
 * legacy uploaded submission with the same combo still requires its document.
 */
export function requiresSubmissionDocument(
  submissionType: string | null | undefined,
  submissionMethod: string | null | undefined,
  hasSubmissionSteps: boolean
): boolean {
  const form = getWasteSubmissionForm(submissionType, submissionMethod);
  if (form === undefined) {
    return true;
  }
  return FORM_CAPABILITIES[form].requiresSubmissionDocument || !hasSubmissionSteps;
}
